import type { Channel } from "@/mocks/streams";
import { formatViewers, formatDuration } from "@/lib/format";

interface HostCardProps {
  channel: Channel;
  isFavorite: boolean;
  isFollowed: boolean;
  onToggleFavorite: () => void;
  onToggleFollow: () => void;
}

export default function HostCard({ channel, isFavorite, isFollowed, onToggleFavorite, onToggleFollow }: HostCardProps) {
  return (
    <div className="rounded-xl border border-background-200/70 bg-background-100 p-5">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-full border border-background-300">
          <img src={channel.hostAvatar} alt={`主播 ${channel.hostName}`} className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate font-heading text-base font-semibold text-foreground-950">{channel.hostName}</h2>
            <span
              className={`rounded-md px-1.5 py-0.5 text-[10px] ${
                channel.hostLevel === "签约" ? "bg-accent-500/15 text-accent-400" : "bg-secondary-100/10 text-foreground-400"
              }`}
            >
              {channel.hostLevel}主播
            </span>
          </div>
          <p className="mt-1 text-xs text-foreground-400">
            {channel.isLive ? "正在直播中" : "当前未开播"} · {formatViewers(channel.viewers)} 人气
          </p>
        </div>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-foreground-500">{channel.hostBio}</p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onToggleFollow}
          className={`rounded-md py-2.5 text-sm transition-colors whitespace-nowrap ${
            isFollowed
              ? "bg-background-200 text-foreground-500 hover:text-foreground-800"
              : "bg-accent-500 text-background-50 hover:bg-accent-600"
          }`}
        >
          {isFollowed ? "已关注主播" : "+ 关注主播"}
        </button>
        <button
          type="button"
          onClick={onToggleFavorite}
          className={`flex items-center justify-center gap-1 rounded-md py-2.5 text-sm transition-colors whitespace-nowrap ${
            isFavorite
              ? "bg-primary-500/15 text-primary-500"
              : "border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-500"
          }`}
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={isFavorite ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
          </span>
          {isFavorite ? "已收藏" : "收藏频道"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 border-t border-background-200/70 pt-5 text-center">
        <div>
          <p className="font-heading text-base font-semibold text-foreground-950">
            {channel.isLive ? formatDuration(channel.startedMinutes) : "—"}
          </p>
          <p className="mt-1 text-xs text-foreground-400">已开播时长</p>
        </div>
        <div>
          <p className="font-heading text-base font-semibold text-foreground-950">{channel.language}</p>
          <p className="mt-1 text-xs text-foreground-400">直播语言</p>
        </div>
      </div>
    </div>
  );
}